import { useMemo } from 'react';
import { productById } from '../game/constants.js';
import './MikeysPlaceScene.css';

const money = (value) => `$${Math.max(0, Math.floor(Number(value) || 0)).toLocaleString()}`;
const qty = (value) => Number(value || 0).toFixed(1).replace(/\.0$/, '');

function barterQuote(character, cashValue) {
  const barter = character?.barter || {};
  const remaining = Math.max(0, Number(barter.smokesRemaining || 0));
  const smokePrice = Math.max(1, Number(barter.smokeReferencePrice || 8));
  const bonus = Math.max(0, Number(barter.valueBonusPercent || 0)) / 100;
  if (remaining <= 0 || cashValue <= 0) return null;
  const targetTotal = Math.max(1, Math.floor(cashValue * (1 + bonus)));
  const smokeTargetValue = Math.max(smokePrice, Math.floor(targetTotal * .40));
  let packs = Math.min(remaining, Math.max(1, Math.floor(smokeTargetValue / smokePrice)));
  while (packs > 0 && packs * smokePrice >= targetTotal) packs -= 1;
  if (packs <= 0) return null;
  const cash = Math.max(1, targetTotal - packs * smokePrice);
  return { cash, packs, effectiveValue: cash + packs * smokePrice };
}

function DealButton({ disabled, onClick, children, detail, tone = '' }) {
  return (
    <button className={`mikey-choice ${tone}`.trim()} disabled={disabled} onClick={onClick}>
      <strong>{children}</strong>
      {detail && <small>{detail}</small>}
    </button>
  );
}

function ProductDeals({ productId, offer, held, character, busy, onSell }) {
  const product = productById(productId);
  const demand = Number(offer?.demandRemaining || 0);
  const unitPrice = Number(offer?.unitPrice || 0);
  const options = useMemo(
    () => (offer?.dealSizes || []).map(Number).filter((amount) => amount > 0 && amount <= held + 1e-9 && amount <= demand + 1e-9),
    [offer?.dealSizes, held, demand],
  );

  return (
    <section className="mikey-product">
      <header>
        <div>
          <span className="mikey-product-name">{product?.name || productId}</span>
          <small>MIKEY PAYS <b>+{Number(offer?.premiumPercent || 0)}%</b> OVER MT. GATE</small>
        </div>
        <strong>{money(unitPrice)}/g</strong>
      </header>
      <div className="mikey-demand">You have {qty(held)}g · Mikey still wants {qty(demand)}g today</div>
      {options.length > 0 ? options.map((amount) => {
        const cashValue = Math.floor(amount * unitPrice);
        const smokeQuote = offer?.barterAvailable ? barterQuote(character, cashValue) : null;
        return (
          <div className="mikey-deal-row" key={`${productId}-${amount}`}>
            <DealButton disabled={busy} onClick={() => onSell(productId, amount, 'cash')} detail={`Cash only · ${money(cashValue)}`} tone="cash">
              Sell {qty(amount)}g
            </DealButton>
            {smokeQuote && (
              <DealButton disabled={busy} onClick={() => onSell(productId, amount, 'barter')} detail={`Effective value ${money(smokeQuote.effectiveValue)} · ~${Number(character?.barter?.valueBonusPercent || 0)}% better`} tone="smokes">
                {money(smokeQuote.cash)} + {smokeQuote.packs} pack{smokeQuote.packs === 1 ? '' : 's'} smokes
              </DealButton>
            )}
          </div>
        );
      }) : <p className="mikey-empty">You do not have enough carried {product?.name || productId}, or Mikey has already burned through today’s demand.</p>}
      {offer?.barterAvailable && !barterQuote(character, Math.max(1, unitPrice)) && Number(character?.barter?.smokesRemaining || 0) <= 0 && (
        <p className="mikey-note">Mikey is out of smoke barter for today. Cash still works.</p>
      )}
    </section>
  );
}

export function MikeysPlaceScene({ gameState, busy, actions }) {
  const character = gameState.specialCharacter;
  const offers = character?.offers || {};
  const budHeld = Number(gameState.inventory?.bud || 0);
  const methHeld = Number(gameState.inventory?.meth || 0);
  const cash = Number(gameState.cash || 0);
  const smokes = Number(gameState.streetInventory?.smokes || 0);
  const bikeOwned = Boolean(gameState.travelGear?.bicycle?.owned);

  if (character?.available === false) {
    return (
      <div className="mikey-overlay mikey-away-overlay" role="dialog" aria-modal="true" aria-labelledby="mikey-title">
        <div className="mikey-away-card">
          <span className="scene-kicker">MT. GATE · MIKEY’S PLACE</span>
          <h2 id="mikey-title">{character.unavailableTitle || 'MIKEY AIN’T HOME'}</h2>
          <p>{character.unavailableBody || 'Nobody answers. Mikey is gone for the day.'}</p>
          <DealButton disabled={busy} onClick={() => actions.travel('trailer_park')} detail="Head back into Mt. Gate" tone="leave">Leave</DealButton>
        </div>
      </div>
    );
  }

  return (
    <div className="mikey-overlay" role="dialog" aria-modal="true" aria-labelledby="mikey-title">
      <div className="mikey-frame">
        <div className="mikey-art-wrap">
          <img className="mikey-art" src="./scenes/mikeys-place.webp" alt="Mikey sitting in his cluttered Mt. Gate hangout" />
          <div className="mikey-art-shade" aria-hidden="true" />
          <div className="mikey-label">MT. GATE · MIKEY’S PLACE</div>
          <div className="mikey-wallet">CASH <strong>{money(cash)}</strong> · SMOKES <strong>{smokes}</strong></div>
          {bikeOwned && <div className="mikey-bike-warning">BIKE PARKED OUTSIDE · MAYBE KEEP AN EYE ON IT</div>}
        </div>
        <div className="mikey-lower">
          <section className="mikey-copy">
            <span className="scene-kicker">SPECIAL BUYER</span>
            <h2 id="mikey-title">Mikey’s Place</h2>
            <p>Mikey is dirty, gullible, and absolutely convinced he knows a good deal when he sees one. He pays stupid money for weed and meth, and sometimes sweetens the deal with cigarettes.</p>
            {character ? (
              <div className="mikey-facts">
                <span><b>DAY {character.day}</b><small>Today’s quotes stay fixed</small></span>
                <span><b>{Number(character.barter?.smokesRemaining || 0)} / {Number(character.barter?.smokesDailyCap || 10)}</b><small>Smoke packs still available</small></span>
                <span><b>{bikeOwned ? '15% RISK' : 'NO BIKE'}</b><small>{bikeOwned ? 'Once today when you leave' : 'Nothing parked to steal'}</small></span>
              </div>
            ) : <p className="mikey-note">Mikey isn’t taking deals on this run. This location requires the current server-side Mikey content pack.</p>}
          </section>
          <section className="mikey-choices">
            {offers.bud && <ProductDeals productId="bud" offer={offers.bud} held={budHeld} character={character} busy={busy} onSell={actions.mikeySell} />}
            {offers.meth && <ProductDeals productId="meth" offer={offers.meth} held={methHeld} character={character} busy={busy} onSell={actions.mikeySell} />}
            {character && !offers.bud && !offers.meth && <p className="mikey-empty">Mikey has nothing useful to offer today.</p>}
            <DealButton disabled={busy} onClick={() => actions.travel('trailer_park')} detail={bikeOwned ? 'Head back into Mt. Gate · your parked bicycle may or may not still exist' : 'Walk back toward the Trailer Park'} tone="leave">
              Leave Mikey’s Place
            </DealButton>
          </section>
        </div>
        {busy && <div className="mikey-busy">MIKEY IS COUNTING MONEY WITH HIS LIPS MOVING…</div>}
      </div>
    </div>
  );
}
